/**
 * IR8A Export Tab
 *
 * Primary: IRAS myTax Portal bulk-upload. Aggregates employment income +
 * deductions (from payroll) and benefits-in-kind (from Appendix 8A records)
 * into the prescribed IRAS "IR8A Import Template" (.xlsx) that HR drag-and-drops
 * into myTax Portal > Employers > Submit Employment Income Records.
 *
 * Secondary: the Appendix 8A (Benefits-in-Kind) generation/verification/CSV
 * workflow that feeds the BIK column (Form IR8A item d8).
 *
 * Design System: Admin Theme (Red)
 */

import React, { useState, useMemo, useEffect } from 'react';
import LoadingSpinner from '../../../../components/UI/LoadingSpinner';
import { Pagination } from '../../../../components/UI/Pagination';
import {
  FileText,
  FileSpreadsheet,
  Download,
  CheckCircle,
  Clock,
  Send,
  RefreshCw,
  User,
  Users,
  Eye,
  AlertTriangle,
  Info,
  X,
  Files,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { FilterButton } from '../../../../components/UI/FilterButton';
import {
  useIR8ARecords,
  useIR8ASummary,
  useGenerateIR8A,
  useVerifyIR8ARecord,
  useSubmitIR8ARecords,
  useExportIR8ACSV,
  useIR8AMyTaxPreview,
  useExportIR8AMyTax,
} from '../../../../hooks/useBIKData';
import Modal from '../../../../components/UI/Modal';
import IconButton from '../../../../components/UI/IconButton';
import DebouncedSearchInput from '../../../../components/UI/DebouncedSearchInput';

// Whole-dollar formatting (IRAS reports income/deductions in full dollars).
const formatDollars = (value) =>
  `$${parseInt(value || 0, 10).toLocaleString('en-SG')}`;

const formatMoney = (value) =>
  `$${parseFloat(value || 0).toLocaleString('en-SG', { minimumFractionDigits: 2 })}`;

const PAGE_SIZE = 25;

const APPENDIX8A_STATUS_ORDER = { draft: 0, amended: 1, verified: 2, submitted: 3 };

/** HR review order: data-quality issues first, then A–Z. */
const sortMyTaxRows = (rows) =>
  [...rows].sort((a, b) => {
    const aHasIssues = (a.issues?.length || 0) > 0;
    const bHasIssues = (b.issues?.length || 0) > 0;
    if (aHasIssues !== bHasIssues) return aHasIssues ? -1 : 1;
    return (a.full_name || a.staff_id || '').localeCompare(
      b.full_name || b.staff_id || '',
      'en-SG',
      { sensitivity: 'base' },
    );
  });

/** HR workflow order: drafts to verify → amended → ready → submitted. */
const sortAppendix8ARecords = (records) =>
  [...records].sort((a, b) => {
    const statusDiff =
      (APPENDIX8A_STATUS_ORDER[a.status] ?? 99) -
      (APPENDIX8A_STATUS_ORDER[b.status] ?? 99);
    if (statusDiff !== 0) return statusDiff;
    return (a.employee_name || a.staff_id || '').localeCompare(
      b.employee_name || b.staff_id || '',
      'en-SG',
      { sensitivity: 'base' },
    );
  });

const IR8AExportTab = ({ year }) => {
  const [bikOpen, setBikOpen] = useState(false);

  return (
    <div className="space-y-6">
      <MyTaxSection year={year} />

      {/* Secondary: Appendix 8A (Benefits-in-Kind) workflow */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <button
          onClick={() => setBikOpen((v) => !v)}
          className="w-full flex items-center justify-between gap-3 px-4 sm:px-6 py-4 text-left hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-3 min-w-0">
            {bikOpen ? (
              <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
            ) : (
              <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
            )}
            <div className="min-w-0">
              <h2 className="text-base sm:text-lg font-semibold text-gray-900 truncate">
                Benefits-in-Kind (Appendix 8A)
              </h2>
              <p className="text-xs sm:text-sm text-gray-600 truncate">
                Feeds Form IR8A item d8. Generate &amp; verify BIK, or export the Appendix 8A CSV.
              </p>
            </div>
          </div>
        </button>
        {bikOpen && (
          <div className="border-t border-gray-200 p-4 sm:p-6">
            <Appendix8ASection year={year} />
          </div>
        )}
      </div>
    </div>
  );
};

// =============================================================================
// PRIMARY: myTax Portal bulk-upload
// =============================================================================
const MyTaxSection = ({ year }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [issuesOnly, setIssuesOnly] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const { data: preview, isLoading, refetch, isFetching } = useIR8AMyTaxPreview(year);
  const exportMyTax = useExportIR8AMyTax();

  const rows = preview?.rows || [];
  const totals = preview?.totals || {};

  const filteredRows = useMemo(() => {
    return rows.filter((r) => {
      const matchesSearch =
        !searchTerm ||
        r.staff_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        r.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        r.id_no?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesIssues = !issuesOnly || (r.issues && r.issues.length > 0);
      return matchesSearch && matchesIssues;
    });
  }, [rows, searchTerm, issuesOnly]);

  const sortedRows = useMemo(() => sortMyTaxRows(filteredRows), [filteredRows]);

  const totalPages = Math.ceil(sortedRows.length / PAGE_SIZE);

  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return sortedRows.slice(start, start + PAGE_SIZE);
  }, [sortedRows, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, issuesOnly, year]);

  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const handleDownload = () => exportMyTax.mutate({ year });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const employeeCount = preview?.employee_count || 0;
  const filesNeeded = preview?.files_needed || 0;
  const withIssues = preview?.employees_with_issues || 0;
  const withBik = preview?.employees_with_bik || 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 truncate">
              IR8A — myTax Portal Submission ({year})
            </h2>
            <p className="text-xs sm:text-sm text-gray-600">
              Year of Assessment {year + 1} · Download the official IRAS IR8A Import Template
            </p>
          </div>

          <div className="flex flex-wrap justify-end gap-2 sm:gap-3 flex-shrink-0">
            <IconButton
              icon={RefreshCw}
              onClick={() => refetch()}
              loading={isFetching}
              title="Refresh"
              ariaLabel="Refresh IR8A preview"
              variant="default"
            />
            <button
              onClick={handleDownload}
              disabled={exportMyTax.isPending || employeeCount === 0}
              className="btn btn-admin-primary text-sm"
            >
              {exportMyTax.isPending ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <FileSpreadsheet className="w-4 h-4 mr-2" />
              )}
              Download myTax Template
            </button>
          </div>
        </div>
      </div>

      {/* How it works */}
      <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 sm:p-5">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-xs sm:text-sm text-blue-900 space-y-2">
            <p className="font-semibold">myTax Portal bulk upload (Submit Employment Income Records)</p>
            <p>
              Downloads the prescribed IRAS <span className="font-medium">IR8A Import Template</span> with the
              <span className="font-medium"> IR8A Details</span> tab (income &amp; deductions) and
              <span className="font-medium"> Appendix 8A</span> tabs (benefits-in-kind) pre-filled. In myTax Portal go to
              <span className="font-medium"> Employers → Submit Employment Income Records</span>, then drag-and-drop the file.
            </p>
            <p>
              IRAS allows max <span className="font-medium">200 records per file</span>. With more employees the download is a
              <span className="font-medium"> .zip of 200-record files</span> — submit each separately.
            </p>
            <p className="text-blue-800">
              <span className="font-medium">Complete in-portal where applicable:</span> director's fees, Appendix 8B (share options),
              IR8S (excess/voluntary CPF), and life-insurance premiums are not auto-filled. An authorised Corppass
              <span className="font-medium"> Approver</span> must submit by <span className="font-medium">1 March</span>.
            </p>
          </div>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
        <SummaryCard icon={Users} tone="gray" value={employeeCount} label="Employees" />
        <SummaryCard icon={Files} tone="blue" value={filesNeeded} label={`File${filesNeeded === 1 ? '' : 's'} to submit`} />
        <SummaryCard icon={FileText} tone="green" value={withBik} label="With Benefits-in-Kind" />
        <SummaryCard
          icon={AlertTriangle}
          tone={withIssues > 0 ? 'amber' : 'gray'}
          value={withIssues}
          label="Need attention"
        />
      </div>

      {/* Income totals strip */}
      <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 text-center">
          <TotalItem label="Gross Salary" value={totals.gross_salary} />
          <TotalItem label="Bonus" value={totals.bonus} />
          <TotalItem label="Allowances" value={totals.allowances} />
          <TotalItem label="Commission" value={totals.gross_commission} />
          <TotalItem label="Employee CPF" value={totals.employee_cpf} />
          <TotalItem label="BIK (d8)" value={totals.bik_total} />
        </div>
      </div>

      {/* Data-quality warning */}
      {withIssues > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900">
            <span className="font-medium">{withIssues} employee(s)</span> are missing mandatory IRAS fields
            (NRIC/FIN, sex or date of birth). They are still included in the file, but IRAS will reject the upload
            until these are filled. Use the filter below to review them.
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-2xl shadow-sm border p-4">
        <div className="flex items-center gap-3">
          <DebouncedSearchInput
            value={searchTerm}
            onSearch={setSearchTerm}
            placeholder="Search by name, staff ID or NRIC/FIN..."
            className="max-w-xl"
          />
          <FilterButton
            theme="admin"
            isOpen={showFilters}
            activeCount={issuesOnly ? 1 : 0}
            onClick={() => setShowFilters((v) => !v)}
          />
        </div>
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={issuesOnly}
                onChange={(e) => setIssuesOnly(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-red-600 focus-visible:ring-red-500"
              />
              Show only employees needing attention
            </label>
          </div>
        )}
      </div>

      {/* Preview table / cards */}
      {filteredRows.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm border px-6 py-12 text-center text-gray-500">
          <FileSpreadsheet className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p>No employee income records found for {year}</p>
          <p className="text-sm mt-1">Records are built from finalised payroll for the calendar year.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
          {(searchTerm || issuesOnly) && (
            <div className="px-4 pt-3 text-xs text-gray-500">
              {sortedRows.length} of {rows.length} employee{rows.length === 1 ? '' : 's'}
              {issuesOnly ? ' needing attention' : ''}
            </div>
          )}

          {/* Mobile cards */}
          <div className="md:hidden space-y-3 p-4">
            {paginatedRows.map((r) => (
              <div
                key={r.staff_id}
                className={`border rounded-xl p-4 border-gray-200 ${r.issues?.length ? 'bg-amber-50/40' : 'bg-white'}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{r.full_name || r.staff_id}</p>
                    <p className="text-xs text-gray-500">
                      {r.staff_id} · {r.id_type ? `${r.id_type} ${r.id_no}` : 'No ID'}
                    </p>
                  </div>
                  {r.issues?.length > 0 ? (
                    <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-800 flex-shrink-0">
                      <AlertTriangle className="w-3 h-3" />
                      Needs info
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800 flex-shrink-0">
                      <CheckCircle className="w-3 h-3" />
                      Ready
                    </span>
                  )}
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                  <Field label="Gross Salary" value={formatDollars(r.gross_salary)} />
                  <Field label="Bonus" value={formatDollars(r.bonus)} />
                  <Field label="Allowances" value={formatDollars(r.allowances)} />
                  <Field label="Commission" value={formatDollars(r.gross_commission)} />
                  <Field label="Employee CPF" value={formatDollars(r.employee_cpf)} />
                  <Field label="BIK (d8)" value={formatDollars(r.bik_total)} />
                </div>
                {r.issues?.length > 0 && (
                  <p className="mt-2 text-xs text-amber-700">{r.issues.join(', ')}</p>
                )}
              </div>
            ))}
          </div>

          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="table-header">
                <tr>
                  <th className="table-header-cell">Employee</th>
                  <th className="table-header-cell">ID</th>
                  <th className="table-header-cell text-right">Gross Salary</th>
                  <th className="table-header-cell text-right">Bonus</th>
                  <th className="table-header-cell text-right">Allowances</th>
                  <th className="table-header-cell text-right">Commission</th>
                  <th className="table-header-cell text-right">Emp. CPF</th>
                  <th className="table-header-cell text-right">BIK (d8)</th>
                  <th className="table-header-cell text-center">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedRows.map((r) => (
                  <tr key={r.staff_id} className={`table-row-dense ${r.issues?.length ? 'bg-amber-50/40' : ''}`}>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <User className="w-4 h-4 text-gray-500" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{r.full_name || r.staff_id}</p>
                          <p className="text-xs text-gray-500">{r.staff_id}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-900">
                        {r.id_type ? (
                          <>
                            <span className="text-xs text-gray-500">{r.id_type}</span>{' '}
                            {r.id_no}
                          </>
                        ) : (
                          <span className="text-amber-600">—</span>
                        )}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right text-sm text-gray-900 tabular-nums">{formatDollars(r.gross_salary)}</td>
                    <td className="px-6 py-4 text-right text-sm text-gray-900 tabular-nums">{formatDollars(r.bonus)}</td>
                    <td className="px-6 py-4 text-right text-sm text-gray-900 tabular-nums">{formatDollars(r.allowances)}</td>
                    <td className="px-6 py-4 text-right text-sm text-gray-900 tabular-nums">{formatDollars(r.gross_commission)}</td>
                    <td className="px-6 py-4 text-right text-sm text-gray-900 tabular-nums">{formatDollars(r.employee_cpf)}</td>
                    <td className="px-6 py-4 text-right text-sm font-semibold text-gray-900 tabular-nums">{formatDollars(r.bik_total)}</td>
                    <td className="px-6 py-4 text-center">
                      {r.issues?.length > 0 ? (
                        <span
                          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-800"
                          title={r.issues.join(', ')}
                        >
                          <AlertTriangle className="w-3 h-3" />
                          Needs info
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                          <CheckCircle className="w-3 h-3" />
                          Ready
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            totalCount={sortedRows.length}
            pageSize={PAGE_SIZE}
            theme="admin"
            noun="employees"
            className="rounded-b-2xl"
          />
        </div>
      )}
    </div>
  );
};

const SummaryCard = ({ icon: Icon, tone, value, label }) => {
  const tones = {
    gray: 'bg-gray-100 text-gray-600',
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    amber: 'bg-amber-100 text-amber-600',
  };
  return (
    <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
      <div className="flex items-center">
        <div className={`w-9 h-9 sm:w-12 sm:h-12 rounded-full flex items-center justify-center flex-shrink-0 ${tones[tone]}`}>
          <Icon className="w-5 h-5 sm:w-6 sm:h-6" />
        </div>
        <div className="ml-2 sm:ml-4 min-w-0">
          <p className="text-lg sm:text-2xl font-bold text-gray-900">{value}</p>
          <p className="text-xs sm:text-sm text-gray-600 truncate">{label}</p>
        </div>
      </div>
    </div>
  );
};

const TotalItem = ({ label, value }) => (
  <div className="min-w-0">
    <p className="text-xs text-gray-500 truncate">{label}</p>
    <p className="text-sm sm:text-base font-semibold text-gray-900 tabular-nums">{formatDollars(value)}</p>
  </div>
);

const Field = ({ label, value }) => (
  <div className="flex items-center justify-between gap-2">
    <span className="text-gray-500">{label}</span>
    <span className="text-gray-900 tabular-nums">{value}</span>
  </div>
);

// =============================================================================
// SECONDARY: Appendix 8A (Benefits-in-Kind) workflow
// =============================================================================
const Appendix8ASection = ({ year }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const { data: records = [], isLoading, refetch } = useIR8ARecords({ year });
  const { data: summary } = useIR8ASummary(year);

  const generateIR8A = useGenerateIR8A();
  const verifyRecord = useVerifyIR8ARecord();
  const submitRecords = useSubmitIR8ARecords();
  const exportCSV = useExportIR8ACSV();

  const filteredRecords = useMemo(() => {
    return records.filter((record) => {
      const matchesSearch =
        !searchTerm ||
        record.staff_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        record.employee_name?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = !filterStatus || record.status === filterStatus;
      return matchesSearch && matchesStatus;
    });
  }, [records, searchTerm, filterStatus]);

  const sortedRecords = useMemo(() => sortAppendix8ARecords(filteredRecords), [filteredRecords]);

  const totalPages = Math.ceil(sortedRecords.length / PAGE_SIZE);

  const paginatedRecords = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return sortedRecords.slice(start, start + PAGE_SIZE);
  }, [sortedRecords, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterStatus, year]);

  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const toggleRecordSelection = (recordId) => {
    setSelectedRecords((prev) =>
      prev.includes(recordId) ? prev.filter((id) => id !== recordId) : [...prev, recordId]
    );
  };

  const handleGenerate = () => generateIR8A.mutate({ year });
  const handleVerify = (recordId) => verifyRecord.mutate({ id: recordId, notes: '' });
  const handleExportCSV = () => exportCSV.mutate({ year, includeZeroBIK: false });

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft': return 'bg-yellow-100 text-yellow-800';
      case 'verified': return 'bg-blue-100 text-blue-800';
      case 'submitted': return 'bg-green-100 text-green-800';
      case 'amended': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'draft': return Clock;
      case 'verified': return CheckCircle;
      case 'submitted': return Send;
      case 'amended': return AlertTriangle;
      default: return Clock;
    }
  };

  const renderStatusBadge = (record) => {
    const StatusIcon = getStatusIcon(record.status);
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full ${getStatusColor(record.status)}`}>
        <StatusIcon className="w-3 h-3" />
        {record.status}
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <LoadingSpinner size="md" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Actions */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-4 text-sm text-gray-600">
          <span>Total: <span className="font-semibold text-gray-900">{summary?.total_records || 0}</span></span>
          <span>Draft: <span className="font-semibold text-gray-900">{summary?.by_status?.draft?.count || 0}</span></span>
          <span>Ready: <span className="font-semibold text-gray-900">{summary?.ready_for_submission || 0}</span></span>
          <span>Submitted: <span className="font-semibold text-gray-900">{summary?.already_submitted || 0}</span></span>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleGenerate}
            disabled={generateIR8A.isPending}
            className="btn btn-admin-outline text-sm py-1.5"
          >
            {generateIR8A.isPending ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <FileText className="w-4 h-4 mr-2" />
            )}
            Generate BIK
          </button>
          <button
            onClick={handleExportCSV}
            disabled={exportCSV.isPending}
            className="btn btn-admin-outline text-sm py-1.5"
          >
            <Download className="w-4 h-4 mr-2" />
            Appendix 8A CSV
          </button>
        </div>
      </div>

      {/* Bulk actions */}
      {selectedRecords.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-red-800">{selectedRecords.length} selected</span>
            <div className="flex gap-3">
              <button onClick={() => setSelectedRecords([])} className="text-sm text-gray-600 hover:text-gray-800">
                Clear
              </button>
              <button onClick={() => setShowSubmitModal(true)} className="btn btn-admin-primary text-sm py-1.5">
                <Send className="w-3 h-3 mr-1.5" />
                Mark as Submitted
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3">
        <DebouncedSearchInput
          value={searchTerm}
          onSearch={setSearchTerm}
          placeholder="Search by employee..."
          className="max-w-md"
        />
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="select-admin">
          <option value="">All Statuses</option>
          <option value="draft">Draft</option>
          <option value="verified">Verified</option>
          <option value="submitted">Submitted</option>
          <option value="amended">Amended</option>
        </select>
        <IconButton icon={RefreshCw} onClick={() => refetch()} title="Refresh" ariaLabel="Refresh BIK records" variant="default" />
      </div>

      {/* Records */}
      {filteredRecords.length === 0 ? (
        <div className="px-6 py-10 text-center text-gray-500 border border-dashed rounded-xl">
          <FileText className="w-10 h-10 mx-auto mb-3 text-gray-300" />
          <p>No Appendix 8A records for {year}</p>
          <p className="text-sm mt-1">Click "Generate BIK" to create them from verified benefits.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
          {(searchTerm || filterStatus) && (
            <div className="px-4 pt-3 text-xs text-gray-500">
              {sortedRecords.length} of {records.length} record{records.length === 1 ? '' : 's'}
            </div>
          )}

          {/* Mobile cards */}
          <div className="md:hidden space-y-3 p-4">
            {paginatedRecords.map((record) => {
              const isSelected = selectedRecords.includes(record.id);
              const canSelect = record.status === 'verified';
              return (
                <div
                  key={record.id}
                  className={`border rounded-xl p-4 border-gray-200 flex flex-col h-full ${isSelected ? 'bg-red-50' : 'bg-white'}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 min-w-0">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        disabled={!canSelect}
                        onChange={() => toggleRecordSelection(record.id)}
                        className="w-4 h-4 mt-0.5 rounded border-gray-300 text-red-600 focus-visible:ring-red-500 disabled:opacity-50 flex-shrink-0"
                      />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {record.employee_name || record.staff_id}
                        </p>
                        <p className="text-xs text-gray-500">{record.staff_id}</p>
                      </div>
                    </div>
                    {renderStatusBadge(record)}
                  </div>
                  <div className="mt-3 space-y-2 text-sm flex-1">
                    <Field label="Place of Residence" value={formatMoney(record.d2_place_of_residence)} />
                    <Field label="Car Benefit" value={formatMoney(record.d5_motor_vehicle)} />
                    <Field label="Total BIK (d8)" value={formatMoney(record.total_bik_value)} />
                  </div>
                  <div className="card-action-footer">
                    <button
                      onClick={() => setShowDetailModal(record)}
                      className="icon-button-admin"
                      title="View Details"
                      aria-label={`View IR8A details for ${record.employee_name || record.staff_id}`}
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    {record.status === 'draft' && (
                      <button
                        onClick={() => handleVerify(record.id)}
                        disabled={verifyRecord.isPending}
                        className="icon-button-admin"
                        title="Verify"
                        aria-label={`Verify IR8A record for ${record.employee_name || record.staff_id}`}
                      >
                        <CheckCircle className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="table-header">
                <tr>
                  <th className="table-header-cell w-12">
                    <input
                      type="checkbox"
                      checked={
                        selectedRecords.length > 0 &&
                        selectedRecords.length === filteredRecords.filter((r) => r.status === 'verified').length
                      }
                      onChange={() => {
                        const verifiedIds = filteredRecords.filter((r) => r.status === 'verified').map((r) => r.id);
                        setSelectedRecords((prev) => (prev.length === verifiedIds.length ? [] : verifiedIds));
                      }}
                      className="w-4 h-4 rounded border-gray-300 text-red-600 focus-visible:ring-red-500"
                    />
                  </th>
                  <th className="table-header-cell">Employee</th>
                  <th className="table-header-cell text-right">Place of Residence</th>
                  <th className="table-header-cell text-right">Car Benefit</th>
                  <th className="table-header-cell text-right">Total BIK (d8)</th>
                  <th className="table-header-cell text-center">Status</th>
                  <th className="table-header-cell text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedRecords.map((record) => {
                  const isSelected = selectedRecords.includes(record.id);
                  const canSelect = record.status === 'verified';
                  return (
                    <tr key={record.id} className={`table-row-dense ${isSelected ? 'bg-red-50' : ''}`}>
                      <td className="px-6 py-4">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          disabled={!canSelect}
                          onChange={() => toggleRecordSelection(record.id)}
                          className="w-4 h-4 rounded border-gray-300 text-red-600 focus-visible:ring-red-500 disabled:opacity-50"
                        />
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm font-medium text-gray-900">{record.employee_name || record.staff_id}</p>
                        <p className="text-xs text-gray-500">{record.staff_id}</p>
                      </td>
                      <td className="px-6 py-4 text-right text-sm text-gray-900 tabular-nums">{formatMoney(record.d2_place_of_residence)}</td>
                      <td className="px-6 py-4 text-right text-sm text-gray-900 tabular-nums">{formatMoney(record.d5_motor_vehicle)}</td>
                      <td className="px-6 py-4 text-right text-sm font-semibold text-gray-900 tabular-nums">{formatMoney(record.total_bik_value)}</td>
                      <td className="px-6 py-4 text-center">{renderStatusBadge(record)}</td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => setShowDetailModal(record)}
                            className="icon-button-admin"
                            title="View Details"
                            aria-label={`View IR8A details for ${record.employee_name || record.staff_id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          {record.status === 'draft' && (
                            <button
                              onClick={() => handleVerify(record.id)}
                              disabled={verifyRecord.isPending}
                              className="icon-button-admin"
                              title="Verify"
                              aria-label={`Verify IR8A record for ${record.employee_name || record.staff_id}`}
                            >
                              <CheckCircle className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            totalCount={sortedRecords.length}
            pageSize={PAGE_SIZE}
            theme="admin"
            noun="records"
            className="rounded-b-2xl"
          />
        </div>
      )}

      {showSubmitModal && (
        <SubmitModal
          selectedCount={selectedRecords.length}
          onClose={() => setShowSubmitModal(false)}
          onSubmit={(irasReference) => {
            submitRecords.mutate(
              { recordIds: selectedRecords, irasReference },
              {
                onSuccess: () => {
                  setSelectedRecords([]);
                  setShowSubmitModal(false);
                },
              }
            );
          }}
          isPending={submitRecords.isPending}
        />
      )}

      {showDetailModal && <DetailModal record={showDetailModal} onClose={() => setShowDetailModal(null)} />}
    </div>
  );
};

// Submit Modal Component
const SubmitModal = ({ selectedCount, onClose, onSubmit, isPending }) => {
  const [irasReference, setIrasReference] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (irasReference.trim()) {
      onSubmit(irasReference);
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      size="md"
      theme="admin"
      className="max-w-md"
      customHeader={(
        <div className="modal-header">
          <h3 className="modal-title truncate min-w-0">Mark as Submitted</h3>
          <button onClick={onClose} className="icon-button-admin flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
    >
          <form onSubmit={handleSubmit}>
            <p className="text-sm text-gray-600 mb-4">
              You are about to mark {selectedCount} record(s) as submitted to IRAS. Please enter the IRAS submission
              reference number.
            </p>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">IRAS Submission Reference *</label>
              <input
                type="text"
                value={irasReference}
                onChange={(e) => setIrasReference(e.target.value)}
                required
                placeholder="e.g., IR8A-2026-001234"
                className="input-admin w-full"
              />
            </div>

            <div className="flex justify-end gap-3">
              <button type="button" onClick={onClose} className="btn btn-admin-outline">
                Cancel
              </button>
              <button type="submit" disabled={isPending || !irasReference.trim()} className="btn btn-admin-primary">
                {isPending ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Confirm Submission
              </button>
            </div>
          </form>
    </Modal>
  );
};

// Detail Modal Component
const DetailModal = ({ record, onClose }) => {
  const bikItems = [
    { label: 'Hotel Accommodation (Sec 3)', value: record.d1_hotel_accommodation },
    { label: 'Place of Residence (Sec 1-2)', value: record.d2_place_of_residence },
    { label: 'Utilities & Housekeeping (Sec 2g-2j)', value: record.d3_utilities },
    { label: 'Other Benefits - home leave/insurance/awards (Sec 4)', value: record.d4_other_benefits },
    { label: 'Car Benefit - 3/7 rule (Sec 4i)', value: record.d5_motor_vehicle },
    { label: 'Driver', value: record.d6_driver },
    { label: 'Pension Contribution (IR8A d5 - not Appendix 8A)', value: record.d7_pension_contribution },
    { label: 'Non-Monetary Awards/Benefits (Sec 4j)', value: record.d9_other_awards },
  ];

  return (
    <Modal
      isOpen
      onClose={onClose}
      size="lg"
      theme="admin"
      className="max-w-2xl"
      contentClassName="space-y-6"
      customHeader={(
        <div className="modal-header">
          <div className="min-w-0">
            <h2 className="modal-title truncate">IR8A Appendix 8A Details</h2>
            <p className="text-xs sm:text-sm text-gray-600 truncate">
              {record.employee_name} ({record.staff_id})
            </p>
          </div>
          <button onClick={onClose} className="icon-button-admin flex-shrink-0" title="Close" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>
      )}
      footer={(
        <button onClick={onClose} className="btn btn-admin-outline">
          Close
        </button>
      )}
    >
          <div>
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Employee Information</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Name:</span>
                  <span className="ml-2 text-gray-900">{record.employee_name}</span>
                </div>
                <div>
                  <span className="text-gray-500">Staff ID:</span>
                  <span className="ml-2 text-gray-900">{record.staff_id}</span>
                </div>
                <div>
                  <span className="text-gray-500">Designation:</span>
                  <span className="ml-2 text-gray-900">{record.employee_designation || '-'}</span>
                </div>
                <div>
                  <span className="text-gray-500">Year:</span>
                  <span className="ml-2 text-gray-900">{record.year} (YOA {record.year + 1})</span>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Benefits in Kind Breakdown</h3>
              <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                {bikItems.map((item, index) => (
                  <div key={index} className="flex justify-between text-sm">
                    <span className="text-gray-600">{item.label}</span>
                    <span className="text-gray-900">{formatMoney(item.value)}</span>
                  </div>
                ))}
                <div className="flex justify-between text-sm font-semibold border-t border-gray-200 pt-3 mt-3">
                  <span className="text-gray-700">Total BIK</span>
                  <span className="text-gray-900">{formatMoney(record.total_bik_value)}</span>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Status & Workflow</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Status:</span>
                  <span className="ml-2 text-gray-900 capitalize">{record.status}</span>
                </div>
                {record.generated_at && (
                  <div>
                    <span className="text-gray-500">Generated:</span>
                    <span className="ml-2 text-gray-900">{new Date(record.generated_at).toLocaleDateString('en-SG')}</span>
                  </div>
                )}
                {record.verified_at && (
                  <div>
                    <span className="text-gray-500">Verified:</span>
                    <span className="ml-2 text-gray-900">{new Date(record.verified_at).toLocaleDateString('en-SG')}</span>
                  </div>
                )}
                {record.submitted_at && (
                  <div>
                    <span className="text-gray-500">Submitted:</span>
                    <span className="ml-2 text-gray-900">{new Date(record.submitted_at).toLocaleDateString('en-SG')}</span>
                  </div>
                )}
                {record.iras_submission_reference && (
                  <div className="col-span-2">
                    <span className="text-gray-500">IRAS Ref:</span>
                    <span className="ml-2 text-gray-900">{record.iras_submission_reference}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
    </Modal>
  );
};

export default IR8AExportTab;
